import type { ChatMessage } from "./types";

export function formatMessageTime(
  value: Date | string | number | undefined,
): string {
  if (value == null) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function createUserMessage(content: string): ChatMessage {
  return {
    id: crypto.randomUUID(),
    role: "user",
    content,
    createdAt: new Date(),
    status: "sent",
  };
}

export function createAssistantMessage(
  content: string,
  partial?: Partial<ChatMessage>,
): ChatMessage {
  const id = partial?.id || partial?.messageID || crypto.randomUUID();
  return {
    role: "assistant",
    content,
    createdAt: new Date(),
    status: "sent",
    ...partial,
    id,
  };
}

const TABLE_SEPARATOR = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

function splitTableRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|") && !row.endsWith("\\|")) row = row.slice(0, -1);
  return row
    .split(/(?<!\\)\|/)
    .map((cell) => cell.replace(/\\\|/g, "|").replace(/<br\s*\/?>/gi, " ").trim());
}

type Block = { type: "text"; line: string } | { type: "table"; rows: string[][]; raw: string };

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const isTableStart =
      lines[i].includes("|") &&
      i + 1 < lines.length &&
      lines[i + 1].includes("-") &&
      TABLE_SEPARATOR.test(lines[i + 1]);
    if (!isTableStart) {
      blocks.push({ type: "text", line: lines[i++] });
      continue;
    }
    const start = i;
    const rows = [splitTableRow(lines[i])];
    i += 2;
    while (i < lines.length && lines[i].trim() !== "" && lines[i].includes("|")) {
      rows.push(splitTableRow(lines[i]));
      i++;
    }
    blocks.push({ type: "table", rows, raw: lines.slice(start, i).join("\n") });
  }
  return blocks;
}

export type MarkdownSegment = { type: "text" | "table"; markdown: string };

export function splitMarkdownByTables(markdown: string): MarkdownSegment[] {
  const segments: MarkdownSegment[] = [];
  let textLines: string[] = [];
  const flushText = () => {
    if (textLines.length) segments.push({ type: "text", markdown: textLines.join("\n") });
    textLines = [];
  };
  for (const b of parseBlocks(markdown)) {
    if (b.type === "text") {
      textLines.push(b.line);
      continue;
    }
    flushText();
    segments.push({ type: "table", markdown: b.raw });
  }
  flushText();
  return segments;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function markdownTablesToTSV(markdown: string, tablesOnly = false): string {
  const blocks = parseBlocks(markdown);
  if (tablesOnly) {
    return blocks
      .filter((b) => b.type === "table")
      .map((b) => b.rows.map((r) => r.join("\t")).join("\n"))
      .join("\n\n");
  }
  return blocks
    .flatMap((b) => (b.type === "text" ? [b.line] : b.rows.map((r) => r.join("\t"))))
    .join("\n");
}

export function markdownTablesToHTML(markdown: string, tablesOnly = false): string {
  const parts: string[] = [];
  for (const b of parseBlocks(markdown)) {
    if (b.type === "text") {
      if (!tablesOnly) parts.push(`<p>${escapeHtml(b.line)}</p>`);
      continue;
    }
    const [header, ...body] = b.rows;
    const th = header.map((c) => `<th>${escapeHtml(c)}</th>`).join("");
    const trs = body
      .map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`)
      .join("");
    parts.push(
      `<table border="1" cellspacing="0" cellpadding="4"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>`
    );
  }
  return parts.join("");
}

export async function copyMarkdownForSlack(markdown: string): Promise<void> {
  const tsv = markdownTablesToTSV(markdown, true);
  if (!tsv) {
    await copyMarkdownToClipboard(markdown);
    return;
  }
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard.write) {
    await navigator.clipboard.writeText(tsv);
    return;
  }
  await navigator.clipboard.write([
    new ClipboardItem({
      "text/plain": new Blob([tsv], { type: "text/plain" }),
      "text/html": new Blob([markdownTablesToHTML(markdown, true)], { type: "text/html" }),
    }),
  ]);
}

export async function copyMarkdownToClipboard(markdown: string): Promise<void> {
  const tsv = markdownTablesToTSV(markdown);
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard.write) {
    await navigator.clipboard.writeText(tsv);
    return;
  }
  await navigator.clipboard.write([
    new ClipboardItem({
      "text/plain": new Blob([tsv], { type: "text/plain" }),
      "text/html": new Blob([markdownTablesToHTML(markdown)], { type: "text/html" }),
    }),
  ]);
}
